import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {StorageService} from '../../services/storage/storage.service';
import {environment} from '../../../environments/environment';
import {BaseService, DataBaseServiceResponse} from '../../services/base-service/base.service';
import {IAuthToken, IUser, IUserLogin} from './user.interface';
import {Router} from '@angular/router';
import {Loader} from '../loader/loader.redux';
import {PreregisterUserDTO} from '../../dtos/user';
import {RolesEnum} from '../../enums/role';
import {firstValueFrom} from 'rxjs';
import {SubscriptionDTO} from '../../dtos/billing';
import {LocalStorageEnum} from '../../enums/utils';
import {PlanActions} from '../plan';
import {RegisterModeConfigEnum} from '../../enums/platform-config';


export interface IRAuth {
  user: IUser;
  userType?: RolesEnum;
  privacyResponse: boolean;
  isLogged: boolean;
  registerMode?: RegisterModeConfigEnum;
}

export namespace AuthActions {
  export class Login {
    static readonly type: string = '[AUTH] Login';
    constructor(
      public login: IUserLogin,
      public clinicId?: string,
      public isInvited?: boolean,
      public userInvitedFromAnotherClinic?: boolean,
      public isMeGuest = false,
    ) { }
  }

  export class InternalLogin {
    static readonly type: string = '[AUTH] Internal Login';
    constructor(
      public login: IUserLogin,
      public clinicId?: string,
      public isInvited?: boolean,
      public userInvitedFromAnotherClinic?: boolean,
      public isMeGuest = false,
    ) { }
  }

  export class SendEmailConfirmation {
    static readonly type: string = '[AUTH] Send Email Confirmation Action';
    constructor() { }
  }

  export class SendCodeConfirmation {
    static readonly type: string = '[AUTH] Send Code Confirmation Action';
    constructor(public code: string) { }
  }

  export class AcceptTermsAndConditions {
    static readonly type: string = '[AUTH] Accept Terms And Conditions Action';
    constructor(public clinicId: string) { }
  }

  export class PreregisterUser {
    static readonly type: string = '[AUTH] Preregister User Action';
    constructor(public user: PreregisterUserDTO, public clinicId: string) { }
  }

  export class RegisterUserOwner {
    static readonly type: string = '[AUTH] Register User Owner Action';
    constructor(public user: PreregisterUserDTO) { }
  }

  export class IsLogged {
    static readonly type: string = '[AUTH] Is Logged';
    constructor() { }
  }

  export class Logout {
    static readonly type: string = '[AUTH] Logout';
    constructor() { }
  }

  export class SetUser {
    static readonly type: string = '[AUTH] Set User Action';
    constructor(public user: Partial<IUser>) { }
  }

  export class RecoveryPassword {
    static readonly type: string = '[AUTH] Recovery Password';
    constructor(public clinicId: string, public email: string) { }
  }

  export class ResetPassword {
    static readonly type: string = '[AUTH] Reset Password';
    constructor(public clinicId: string, public token: string, public password: string) { }
  }

  export class GetUserMe {
    static readonly type: string = '[AUTH] Get User Me Action';
    constructor(
      public clinicId: string,
      public isGuest = true,
    ) { }
  }

  export class GetMyUserType {
    static readonly type: string = '[AUTH] Get My  User Type Action';
    constructor(public clinicId: string) { }
  }

  export class GetAnotherBetaSubscription {
    static readonly type: string = '[AUTH] Get Another Beta Subscription Action';
    constructor() { }
  }

  export class UserIsBetaUserSubscription {
    static readonly type: string = '[AUTH] User Is Beta User Subscription Action';
    constructor(public email: string, public coupon: string) { }
  }

  export class IsBetaUser {
    static readonly type: string = '[AUTH] User Is Beta Action';
    constructor(public email: string) { }
  }

  export class IsAuthBetaUser {
    static readonly type: string = '[AUTH] User Is auth Beta Action';
    constructor() { }
  }

  export class RegisterMode {
    static readonly type: string = '[AUTH] Register Mode Action';
    constructor() { }
  }

  export class GetBetaUserEmailByCoupon {
    static readonly type: string = '[AUTH] Get BetaUser Email By Coupon Action';
    constructor(public encodeCoupon: string) { }
  }
}

@State<IRAuth>({
  name: 'authState',
  defaults: {
    user: null,
    privacyResponse: false,
    isLogged: false,
    registerMode: RegisterModeConfigEnum.ALL
  }
})

@Injectable()
export class AuthState {
  constructor(
    private baseService: BaseService<any>,
    private storageService: StorageService,
    private router: Router,
    private store: Store,
  ) { }

  url = environment.SERVER_URL;

  @Selector()
  public static getUser(state: IRAuth): IUser {
    return state.user;
  }

  @Selector()
  public static getRegisterMode({registerMode}: IRAuth): RegisterModeConfigEnum | undefined  {
    return registerMode;
  }

  @Selector()
  public static getIsPatient(state: IRAuth): boolean {
    return !!state.user?.patients;
  }

  @Selector()
  public static getIsExpert(state: IRAuth): boolean {
    return !!state.user?.experts;
  }

  @Selector()
  public static getIsLogged(state: IRAuth): boolean {
    return !!state.user && state.isLogged;
  }

  @Selector()
  public static getIsLoggedOnly(state: IRAuth): boolean {
    return state.isLogged;
  }

  @Selector()
  public static getPrivacyResponse(state: IRAuth): boolean {
    return state.privacyResponse;
  }

  @Selector()
  public static getUserType(state: IRAuth): RolesEnum {
    return state.userType;
  }

  @Action(AuthActions.Login)
  async login(ctx: StateContext<IRAuth>, action: AuthActions.Login): Promise<void> {
    let options = void 0;
    if (action.isInvited) {
      options = {
        params: {
          guest: true
        }
      };
    }
    const auth = await firstValueFrom((this.baseService.post(`${this.url}/auth/login`, action.login as any, {}, '', options)));
    if (!auth || !auth.success || !auth.entity) {
      throw new Error('Ha ocurrido un error al iniciar sesión');
    }
    if (auth?.entity?.token) {
      this.storageService.setLocalItem('token', auth?.entity?.token);
    }
    if (auth.entity?.clinicId || (action.clinicId !== 'guest' && action.clinicId !== '')) {
      try {
        await firstValueFrom(this.store.dispatch(
          new AuthActions.GetUserMe(action.isInvited ? auth.entity?.clinicId : action.clinicId, action.isMeGuest)
        ));
      } catch (ex) {
        throw new Error(ex.message);
      }
    } else {
      this.router.navigate(['clinic', 'onboarding']).then();
    }
  }

  @Action(AuthActions.GetMyUserType)
  async getMyUserTypeAction(ctx: StateContext<IRAuth>, action: AuthActions.GetMyUserType): Promise<void> {
    const res = await firstValueFrom((this.baseService.get(`${this.url}/clinics/${action.clinicId}/userType`, void 0, void 0)));
    if (res.error) {
      ctx.patchState({
        userType: null
      });
      throw new Error('No se ha podido obtener el tipo de usuario');
    }
    ctx.patchState({
      userType: res.entity.role
    });
  }

  @Action(AuthActions.PreregisterUser)
  async preregisterUserAction(ctx: StateContext<IRAuth>, action: AuthActions.PreregisterUser): Promise<void> {
    const register = await firstValueFrom(this.baseService.post(
      `${this.url}/auth/register/${action.clinicId}/preregister`, action.user, {}, ''));
    if (register.success && register.entity && register?.entity?.token) {
      this.storageService.setLocalItem('token', register?.entity?.token);
      await firstValueFrom((this.store.dispatch(new AuthActions.GetUserMe(action.clinicId))));
    } else {
      throw new Error(register.message);
    }
  }

  @Action(AuthActions.RegisterUserOwner)
  async RegisterUserOwnerAction(ctx: StateContext<IRAuth>, action: AuthActions.RegisterUserOwner): Promise<void> {
    const register: DataBaseServiceResponse<IUser> = await firstValueFrom((this.baseService.post(`${this.url}/auth/register/owner`, action.user, {}, '')));
    if (register.success && register.entity && register?.entity?.token) {
      this.storageService.setLocalItem('token', register?.entity?.token);
    } else {
      throw new Error(register.message);
    }
  }

  @Action(AuthActions.Logout)
  async authLogoutAction(ctx: StateContext<IRAuth>, action: AuthActions.Logout): Promise<void> {
    await firstValueFrom((this.baseService.post(`${this.url}/auth/logout`, {}, void 0)));
    this.storageService.clearSession();
    this.storageService.clearAll();
  }

  @Action(AuthActions.SendEmailConfirmation)
  async sendEmailConfirmationAction(ctx: StateContext<IRAuth>, action: AuthActions.SendEmailConfirmation): Promise<void> {
    const res = await firstValueFrom((this.baseService.post(`${this.url}/users/email/sendConfirmation`, {}, void 0)));
    if (res.error) {
      throw new Error(res.message);
    }
  }

  @Action(AuthActions.SendCodeConfirmation)
  async sendCodeConfirmationAction(ctx: StateContext<IRAuth>, action: AuthActions.SendCodeConfirmation): Promise<void> {
    const res: DataBaseServiceResponse<any> = await firstValueFrom((this.baseService.post(`${this.url}/users/email/confirm`, { code: action.code }, void 0)));
    if (res.error) {
      throw new Error('No se pudo verificar el código. Inténtelo más tarde');
    }
  }

  @Action(AuthActions.AcceptTermsAndConditions)
  async termsAndConditionsAcceptAction(ctx: StateContext<IRAuth>, action: AuthActions.AcceptTermsAndConditions): Promise<void> {
    const res: DataBaseServiceResponse<any> = await firstValueFrom(this.baseService.patch
      (`${this.url}/auth/register/${action.clinicId}/preregister/accept`, { accept: true }, void 0));
    if (res.error) {
      throw new Error('Ha ocurrido un error al aceptar términos y condiciones');
    }
  }

  @Action(AuthActions.RecoveryPassword)
  async recoveryPassword(ctx: StateContext<IRAuth>, action: AuthActions.RecoveryPassword): Promise<void> {
    const body: {email: string} = {
      email: action.email
    };
    const response: DataBaseServiceResponse<any> = await firstValueFrom(
      (this.baseService.post(`${this.url}/auth/${action.clinicId}/recoveryPassword`, body as any, {}, ''))
    );
    if (response.error) {
      throw new Error(response.message);
    }
  }

  @Action(AuthActions.GetUserMe)
  async getUserMeAction(ctx: StateContext<IRAuth>, action: AuthActions.GetUserMe): Promise<void> {
    let options = void 0;
    if (action.isGuest) {
      options = {
        params: {
          guest: true
        }
      };
    }
    const user = await firstValueFrom((this.baseService.get(`${this.url}/users/${action.clinicId}/me`, void 0, '', options)));
    if (!user || user.error) {
      throw new Error(user.message);
    }
    const userBodyResponse: IUser = user.entity;
    const token = this.storageService.getLocalItem('token');
    ctx.patchState({
      user: userBodyResponse
    });
    this.storageService.setLocalItem('user', { token, user: userBodyResponse });
  }

  @Action(AuthActions.SetUser)
  async setUserAction(ctx: StateContext<IRAuth>, action: AuthActions.SetUser): Promise<void> {
    const user: IUser = { ...ctx.getState().user, ...action?.user || {} };
    const token: string = this.storageService.getLocalItem<IAuthToken>('user')?.token;
    this.storageService.setLocalItem('user', { token, user });
    ctx.patchState({ user });
  }

  @Action(AuthActions.ResetPassword)
  async resetPassword(ctx: StateContext<IRAuth>, action: AuthActions.ResetPassword): Promise<void> {
    const body = {
      token: action?.token,
      newPassword: action.password
    };
    const response = await firstValueFrom((this.baseService.post(`${this.url}/auth/${action.clinicId}/resetPassword`, body as any, {}, '')));
    if (!response.success) {
      if (response.code === 409) {
        if (response.message.includes('Expired token')) {
          // token expirado
          throw new Error('Tu token para cambio de contraseña expiró, por favor vuelve a solicitar el cambio de contraseña');
        } else {
          // token inactivo
          throw new Error('Tu token para cambio de contraseña ya fue utilizado, si lo necesitas puedes volver a solicitar el cambio de contraseña');
        }
      } else if (response.code === 404) {
        throw new Error('Token para cambio de contraseña incorrecto, por favor vuelve a solicitar el cambio de contraseña');
      } else {
        throw new Error('Ha ocurrido un error al cambiar la contraseña');
      }
    } else {
      if (response?.entity?.token) {
        this.storageService.setLocalItem('token', response?.entity?.token);
      }
    }
  }

  @Action(AuthActions.IsLogged)
  async isLogged(ctx: StateContext<IRAuth>): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    const res = await firstValueFrom((this.baseService.get(`${this.url}/auth/isLogged`, {})));
    ctx.patchState({
      isLogged: res.entity
    });
  }

  @Action(AuthActions.IsBetaUser)
  async isBetaUserAction(ctx: StateContext<IRAuth>,  action: AuthActions.IsBetaUser): Promise<void> {
    const res: DataBaseServiceResponse<{coupon: string | null}> = await firstValueFrom((this.baseService.get(`${this.url}/beta-user-deployments/is-beta-user?email=${encodeURIComponent(action.email)}`, {})));
    if (res.error) {
      throw new Error(res.message);
    }

    if (res.entity.coupon !== null) {
      this.storageService.setLocalItem(LocalStorageEnum.COUPON_NAME_ACCEPT, res.entity.coupon);
      this.storageService.setLocalItem(LocalStorageEnum.IS_BETA_USER, 'TRUE');
    } else {
      this.storageService.removeLocalItem(LocalStorageEnum.EMAIL_COUPON_ACCEPT);
      this.storageService.removeLocalItem(LocalStorageEnum.COUPON_NAME_ACCEPT);
      this.storageService.removeLocalItem(LocalStorageEnum.IS_BETA_USER);
    }
  }

  @Action(AuthActions.IsAuthBetaUser)
  async isAuthBetaUserAction(): Promise<void> {
    const res: DataBaseServiceResponse<{isBeta: boolean}> =
      await firstValueFrom((this.baseService.get(`${this.url}/beta-user-deployments/auth/is-beta-user`, {})));
    if (res.error) {
      throw new Error(res.message);
    }

    if (res.entity.isBeta) {
      this.storageService.setLocalItem(LocalStorageEnum.IS_BETA_USER, 'TRUE');
    } else {
      this.storageService.removeLocalItem(LocalStorageEnum.IS_BETA_USER);
    }
  }

  @Action(AuthActions.RegisterMode)
  async getRegisterMode(ctx: StateContext<IRAuth>,  action: AuthActions.RegisterMode): Promise<void> {
    const res: DataBaseServiceResponse<{mode: RegisterModeConfigEnum}> = await firstValueFrom(
      this.baseService.get(`${this.url}/beta-user-deployments/only-beta-user`)
    );

    if (res.error) throw new Error(res.message);

    ctx.patchState({
      registerMode: res.entity.mode
    });
  }

  @Action(AuthActions.GetBetaUserEmailByCoupon)
  async getBetaUserEmailByCouponAction(ctx: StateContext<IRAuth>,  {encodeCoupon}: AuthActions.GetBetaUserEmailByCoupon): Promise<void> {
    const request = this.baseService.get(`${this.url}/beta-user-deployments/coupons/${encodeCoupon}`, {});
    const res: DataBaseServiceResponse<{email: string}> = await firstValueFrom(request);
    if (res.error) {
      throw new Error(res.message);
    }

    this.storageService.setLocalItem(LocalStorageEnum.EMAIL_COUPON_ACCEPT, res.entity.email);
    this.storageService.setLocalItem(LocalStorageEnum.IS_BETA_USER, 'TRUE');
  }


  @Action(AuthActions.UserIsBetaUserSubscription)
  async userIsBetaUserSubscriptionAction(ctx: StateContext<IRAuth>,  { email, coupon }: AuthActions.UserIsBetaUserSubscription): Promise<void> {
    const res: DataBaseServiceResponse<any> = await firstValueFrom((this.baseService.post(`${this.url}/beta-user-deployments/subscription`, {email, coupon})));
    if (res.error) {
      throw new Error('Error al registrar la suscripción de prueba');
    }
  }

  @Action(AuthActions.GetAnotherBetaSubscription)
  async getAnotherBetaSubscriptionAction(ctx: StateContext<IRAuth>): Promise<void> {
    const res: DataBaseServiceResponse<SubscriptionDTO> = await firstValueFrom((this.baseService.post(`${this.url}/beta-user-deployments/subscription/another-free-month`, void 0)));
    if (res.error) throw new Error('Error al obtener de nuevo la suscripción de prueba');
    this.store.dispatch(new PlanActions.SetSubscriptions(res.entity));
  }

  @Action(AuthActions.InternalLogin)
  async internalLogin(ctx: StateContext<IRAuth>, action: AuthActions.InternalLogin): Promise<void> {
    let options = void 0;
    if (action.isInvited) {
      options = {
        params: {
          guest: true
        }
      };
    }
    const auth = await firstValueFrom((this.baseService.post(`${this.url}/auth/internal-login`, action.login as any, {}, '', options)));
    if (!auth || !auth.success || !auth.entity) {
      throw new Error('Ha ocurrido un error al iniciar sesión');
    }
    if (auth?.entity?.token) {
      this.storageService.setLocalItem('token', auth?.entity?.token);
    }
    if (auth.entity?.clinicId || (action.clinicId !== 'guest' && action.clinicId !== '')) {
      try {
        await firstValueFrom(this.store.dispatch(
          new AuthActions.GetUserMe(action.isInvited ? auth.entity?.clinicId : action.clinicId, action.isMeGuest)
        ));
      } catch (ex) {
        throw new Error(ex.message);
      }
    } else {
      this.router.navigate(['clinic', 'onboarding']).then();
    }
  }
}
